import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core'
import { Export, ExportType, Supplier } from 'depoto-core/src/entities'
import { ModalContainerComponent } from '../../_common/modal-container/modal-container.component'
import { CoreService, NotificationService } from '../../../services'
import { ListingItem } from 'depoto-core/src/models'
import { exportOrdersColumns } from '../../../utils/definitions.util'
import { BaseListing } from '../../../pages/_base-listing/base-listing'
import * as Sentry from '@sentry/angular-ivy'
import { TranslateService } from '@ngx-translate/core'

type ExportWithDetails = Export & {
  depot?: { id: number; name: string }
  supplier?: { id: number; name: string }
}

const DEFAULT_FILTERS = {
  sortProp: 'created',
  sortDir: 'desc',
}

@Component({
  selector: 'modal-export-create',
  templateUrl: 'modal-export-create.component.html',
  styleUrls: ['modal-export-create.component.scss'],
})
export class ModalExportCreateComponent extends BaseListing {
  @ViewChild('modal') childModal: ModalContainerComponent
  @Input() filters: any
  @Input() entity: string
  @Input() depots
  @Input() suppliers
  @Input() isInFilterForm = false
  @Output() onResult: EventEmitter<any> = new EventEmitter()
  exports: ExportWithDetails[] = []
  listingColumns: ListingItem[] = exportOrdersColumns
  type = ''
  types: ExportType[] = []
  note = ''
  supplier = 0
  depot
  loading = false
  headerTitle: string
  currentPage: number
  sortProp = 'created'
  sortDir = 'desc'
  totalItems = 0
  endPage: number
  defaultFilters = DEFAULT_FILTERS
  fileTypes = ['excel', 'pdf']
  options
  allIntoOneFile = false
  withPrices = 'exports.with.prices'
  withPricesOptions = ['exports.with.prices', 'exports.without.prices']
  date: string
  constructor(
    protected core: CoreService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
  ) {
    super(core)
    this.getStorageKey('exports')
  }

  private extendExport(ex: Export, depots, suppliers): ExportWithDetails {
    try {
      const filterObj = ex.filter?.startsWith('{') ? JSON.parse(ex.filter) : {}
      return {
        ...ex,
        depot: filterObj.depot
          ? { id: Number(filterObj.depot), name: depots.find(d => d.id === Number(filterObj.depot))?.name }
          : undefined,
        supplier:
          filterObj.defaultSuppliers?.length > 1
            ? {
                id: 0,
                name: this.translateService.instant('modal-export-create.all-suppliers'),
              }
            : filterObj.defaultSuppliers?.length > 0
              ? {
                  id: Number(filterObj.defaultSuppliers[0]),
                  name: suppliers.find(s => s.id === Number(filterObj.defaultSuppliers[0]))?.name,
                }
              : undefined,
      }
    } catch (e) {
      console.warn(e)
    }
    return ex
  }

  async getTypes() {
    this.loading = true
    this.types = await this.core.services.export
      .getTypes(
        {},
        {
          id: null,
          name: null,
          format: null,
          group: null,
        },
      )
      .then(res => res.items || [])
    try {
      this.types = <any>this.types
        .filter(t => t.group === this.entity)
        .map(value => {
          return { ...value, name: 'export.type.' + value.id }
        })
      this.type = <any>this.types[0].id
    } catch (e) {
      console.log(e)
    }
    this.loading = false
  }

  async createExportWithFilters(): Promise<void> {
    const exportObject = new Export({
      type: { id: this.type },
      note: this.note,
      filter: JSON.stringify({ ...this.options, date: this.date || null }),
    })

    try {
      const result = await this.core.services.export.create(exportObject)
      this.onResult.emit(result)
      this.notificationService.success(this.translateService.instant('alert.export'))
      this.hideChildModal()
    } catch (err) {
      console.log(err)
    }
  }

  prepareOptions() {
    this.options = {}
    if (this.filters.operationType !== 'all' && this.filters.operationType)
      this.options.type = this.filters.operationType
    if (this.filters.depotFrom > 0) this.options.depotFrom = this.filters.depotFrom
    if (this.filters.depotTo > 0) this.options.depotTo = this.filters.depotTo
    if (this.filters.depot?.length > 0) this.options.depot = this.filters.depot
    if (this.filters.depots?.length > 0) this.options.depots = this.filters.depots
    if (this.filters.fulltext && this.filters.fulltext.length > 0) this.options.fulltext = this.filters.fulltext
    if (this.filters.operationType !== 'transfer') {
      if (this.filters.withoutCheckout || this.filters.checkout === 'none') this.options.withoutCheckout = true
      else if (
        this.filters.checkout !== 'all' &&
        !isNaN(Number(this.filters.checkout)) &&
        Number(this.filters.checkout) > 0
      ) {
        this.options.checkouts = [this.filters.checkout]
      }
    }
    if (this.filters.operationNumber?.length > 0) this.options.number = this.filters.operationNumber
    if (this.filters.reservationNumber?.length > 0) this.options.orderFulltext = this.filters.reservationNumber
    if (this.filters.billNumberFulltext?.length > 0) this.options.orderFulltext = this.filters.billNumberFulltext
    if (this.filters.hasOrder && this.filters.hasOrder !== 'all')
      this.options.withOrder = this.filters.hasOrder === 'yes'
    if (this.filters.productFulltext?.length > 0) this.options.productFulltext = this.filters.productFulltext
    if (this.filters.createdBy && this.filters.createdBy > 0) this.options.createdBy = this.filters.createdBy
    if (this.filters.dateFrom || this.filters.dateTo) {
      this.options.created = {}
      if (this.filters.dateFrom?.length > 0) this.options.created.left_date = this.filters.dateFrom
      if (this.filters.dateTo?.length > 0) this.options.created.right_date = this.filters.dateTo
    }
    if (this.filters.product?.length > 0) this.options.product = this.filters.product
    if (this.filters.batch?.length > 0) this.options.batch = this.filters.batch
    if (this.filters.expirationDate?.length > 0) this.options.expirationDate = this.filters.expirationDate
    if (this.filters.user?.length > 0) this.options.user = this.filters.user
  }

  async checkIfSupplierHasProductsUnderMinimum(supplierId) {
    try {
      const productsOfSupplier = await this.core.services.product.getList(
        {
          page: 1,
          sort: 'id',
          direction: 'asc',
          filters: {
            defaultSuppliers: [Number(supplierId)],
            metricDepots: [Number(this.depot)],
            metrics: 'less_than_minimum',
          },
        },
        { id: null },
      )
      return productsOfSupplier.items?.length > 0
    } catch (err) {
      console.log(err)
      return false
    }
  }

  async generate(): Promise<void> {
    const suppliersWithProductsUnderMinimum: number[] = await this.getSuppliersIdsWithProducts(
      this.supplier === 0 ? this.suppliers.filter(s => s.id !== 0) : this.suppliers.filter(s => s.id === this.supplier),
    )
    if (suppliersWithProductsUnderMinimum?.length < 1) {
      alert(this.translateService.instant('modal-export-create.no-good-under-limit'))
      return
    }
    if (this.supplier === 0 && this.allIntoOneFile) {
      await this.createExport2(suppliersWithProductsUnderMinimum)
      await this.getExports()
    } else {
      for (const supplier of suppliersWithProductsUnderMinimum) {
        await this.createExport2([supplier])
      }
      await this.getExports()
    }
  }

  async getSuppliersIdsWithProducts(suppliersToCheck: Supplier[]): Promise<number[]> {
    const suppliersWithProductsUnderMinimum = []
    await Promise.all(
      suppliersToCheck.map(async supplier => {
        const hasSupplierAProduct = await this.checkIfSupplierHasProductsUnderMinimum(supplier.id)
        if (hasSupplierAProduct) {
          suppliersWithProductsUnderMinimum.push(supplier.id)
        }
      }),
    )
    return suppliersWithProductsUnderMinimum
  }

  async createExport2(suppliers: number[]) {
    const exp = new Export({
      type: { id: this.type === 'excel' ? 'hs-products-for-metrics-excel' : 'hs-products-for-metrics-pdf' },
      filter: JSON.stringify({
        depot: Number(this.depot),
        defaultSuppliers: suppliers,
        withPrice: this.withPrices === 'exports.with.prices',
      }),
    })
    try {
      await this.core.services.export.create(exp)
    } catch (err) {
      console.log(err)
    }
  }

  async getExports() {
    this.startRefreshing('filter')
    await this.filter()
  }

  async filter(pageNo = 1, sortProp: string = this.sortProp, sortDir: string = this.sortDir, concat = false) {
    this.loading = true
    this.currentPage = pageNo
    const filters: any = {
      group: this.isInFilterForm ? this.entity : 'hs-custom',
      types: ['hs-products-for-metrics-excel', 'hs-products-for-metrics-pdf'],
    }
    if (this.date) {
      filters.date = this.date
    }
    if (this.date) {
      filters.date = this.date
    }
    const result = await this.core.services.export.getList(
      { page: pageNo, sort: sortProp, direction: sortDir, filters },
      {
        id: null,
        generated: null,
        created: null,
        url: null,
        type: {
          id: null,
          name: null,
          format: null,
          group: null,
        },
        filter: null,
        createdBy: { id: null, name: null },
        note: null,
      },
    )
    if (!this.isInFilterForm) {
      result?.items?.forEach(i => {
        if (i.type.id === 'hs-products-for-metrics-pdf') i.type.name = 'PDF'
        else if (i.type.id === 'hs-products-for-metrics-excel') i.type.name = 'Excel'
      })
    }
    this.loading = false
    if (!concat) {
      this.exports = result?.items?.map(ex => new Export(ex))
    } else {
      result?.items?.forEach(ex => {
        this.exports.push(new Export(ex))
      })
    }
    this.exports = this.exports?.map(ex => this.extendExport(ex, this.depots, this.suppliers))
    this.totalItems = result?.paginator ? result.paginator.totalCount : 0
    this.endPage = result?.paginator ? result.paginator.endPage : 0
  }

  async showChildModal() {
    this.clearOptions()
    if (this.isInFilterForm) {
      await this.getTypes()
      this.prepareOptions()
    } else {
      this.type = 'excel'
      this.depot = this.depots?.length ? this.depots[0].id : 0
      this.listingColumns = this.listingColumns.filter(column => column.name !== 'note')
      await this.getExports()
    }
    this.childModal.showChildModal()
  }

  clearOptions() {
    this.exports = []
    this.type = ''
    this.types = []
    this.note = ''
    this.supplier = 0
    this.depot = 0
    this.totalItems = 0
    this.defaultFilters = DEFAULT_FILTERS
    this.allIntoOneFile = false
  }

  hideChildModal(): void {
    this.stopRefreshing()
    this.childModal.hideChildModal()
  }

  protected readonly Number = Number
}
